import * as React from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { isSameDay } from 'date-fns'
import { topForTime } from './weekGeometry'

const MINUTE_MS = 60_000

/**
 * When the line next moves: the next minute boundary, so it never lags. Rounds
 * epoch milliseconds, not local fields: in a repeated DST hour the local setters
 * resolve to the first occurrence and land an hour in the past.
 */
export function nextTick(now: Date): Date {
  return new Date((Math.floor(now.getTime() / MINUTE_MS) + 1) * MINUTE_MS)
}

type Props = {
  /** The column's day. */
  day: Date
}

/**
 * A "now" line across today's timed column. It also checks its own day: at
 * midnight its timer fires before the parent moves "today" on.
 */
export function CurrentTimeIndicator({ day }: Props) {
  const theme = useTheme()
  const [now, setNow] = React.useState(() => new Date())

  React.useEffect(() => {
    const id = setTimeout(
      () => setNow(new Date()),
      nextTick(now).getTime() - Date.now(),
    )
    return () => clearTimeout(id)
  }, [now])

  if (!isSameDay(now, day)) return null
  const top = topForTime(now)

  return (
    <Box
      aria-hidden
      data-test-id="current-time-indicator"
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: `${top}px`,
        height: 0,
        borderTop: `2px solid ${theme.palette.error.main}`,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: '-4px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: theme.palette.error.main,
        }}
      />
    </Box>
  )
}
